import { Col, Collapse, Row } from "antd";
import type { CollapseProps } from "antd";
import CollapseExpandIcon from "../CollapseExpandIcon";
import SwitchOptionComponent from "../SwitchComponent";
import InputNumberComponent from "../InputNumberComponent";
import LocalizedContentFields, { LocalizedContentChanges } from "../LocalizedContentFields";
import { Culture, VolunteersHighlightRules } from "../../context/VolunteersHighlightContext/constants";
import {
	GENERAL_SECTION_KEY,
	getDetailSectionKey,
	VolunteersHighlightDetailTexts,
	VolunteersHighlightFormKeys,
	VolunteersHighlightGeneralTexts,
	VolunteersHighlightImageUpload,
	VolunteersHighlightSectionLabels,
} from "./constants";

export interface VolunteersHighlightGeneralValues {
	isActive: boolean;
	displayOrder?: number;
}

export interface VolunteersHighlightLocalizableValues {
	culture: Culture;
	title: string;
	description: string;
	imageUrl?: string;
}

export type VolunteersHighlightLocalizableChanges = LocalizedContentChanges;

export const applyLocalizableChanges = <T extends { culture: Culture }>(
	localizables: T[],
	culture: Culture,
	changes: VolunteersHighlightLocalizableChanges
): T[] => localizables.map(item => (item.culture === culture ? { ...item, ...changes } : item));

interface VolunteersHighlightFormProps {
	general: VolunteersHighlightGeneralValues;
	localizables: VolunteersHighlightLocalizableValues[];
	// On the add form every detail field is required; the update form only sends what changed.
	isRequired: boolean;
	onGeneralChange: (changes: Partial<VolunteersHighlightGeneralValues>) => void;
	onLocalizableChange: (culture: Culture, changes: VolunteersHighlightLocalizableChanges) => void;
}

const VolunteersHighlightForm = ({ general, localizables, isRequired, onGeneralChange, onLocalizableChange }: VolunteersHighlightFormProps) => {
	const items: CollapseProps["items"] = [
		{
			key: GENERAL_SECTION_KEY,
			label: VolunteersHighlightSectionLabels.GENERAL,
			children: (
				<>
					<Row gutter={[20, 16]}>
						<Col xs={24} sm={12} md={8} lg={6}>
							<SwitchOptionComponent
								title={VolunteersHighlightGeneralTexts.isActive.label}
								helperText={VolunteersHighlightGeneralTexts.isActive.helperText}
								value={general.isActive}
								onChange={checked => onGeneralChange({ isActive: checked })}
							/>
						</Col>
					</Row>
					<Row gutter={[20, 16]}>
						<Col xs={24} sm={12} md={6} lg={4}>
							<InputNumberComponent
								title={VolunteersHighlightGeneralTexts.displayOrder.label}
								positiveOnly
								value={general.displayOrder}
								placeholder={VolunteersHighlightGeneralTexts.displayOrder.placeholder}
								onChange={value => onGeneralChange({ displayOrder: value === null || value === "" ? undefined : Number(value) })}
							/>
						</Col>
					</Row>
				</>
			),
		},
		...localizables.map(localizable => {
			const texts = VolunteersHighlightDetailTexts[localizable.culture];

			return {
				key: getDetailSectionKey(localizable.culture),
				label: VolunteersHighlightSectionLabels.getDetail(localizable.culture),
				children: (
					<LocalizedContentFields
						texts={texts}
						title={localizable.title}
						description={localizable.description}
						imageUrl={localizable.imageUrl}
						titleMaxLength={VolunteersHighlightRules.titleMaxLength}
						descriptionMaxLength={VolunteersHighlightRules.descriptionMaxLength}
						imageUpload={VolunteersHighlightImageUpload}
						isRequired={isRequired}
						onChange={changes => onLocalizableChange(localizable.culture, changes)}
					/>
				),
			};
		}),
	];

	return (
		<Collapse
			items={items}
			expandIconPosition="end"
			bordered={false}
			ghost
			expandIcon={CollapseExpandIcon}
			defaultActiveKey={VolunteersHighlightFormKeys}
		/>
	);
};

export default VolunteersHighlightForm;
